import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../../lib/cn';
import {
  CONTROL_CLASSES,
  CONTROL_INVALID_CLASSES,
  Field,
  type FieldOwnProps,
} from '../Field/Field';

/**
 * docs/02-DESIGN-SYSTEM.md §4.1 — grey-100 fill, no visible border at rest,
 * navy 2px focus ring, `radius-md`, label above, error below in `--danger`.
 */
export interface InputProps
  extends
    FieldOwnProps,
    Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'className' | 'required'> {
  /** Leading decoration, e.g. a search icon. Never the only label. */
  icon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hideLabel, hint, error, required, id, className, icon, type = 'text', ...props },
  ref,
) {
  return (
    <Field
      label={label}
      hideLabel={hideLabel}
      hint={hint}
      error={error}
      required={required}
      id={id}
      className={className}
    >
      {(field) => (
        <div className="relative">
          {icon ? (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-4 text-secondary"
            >
              {icon}
            </span>
          ) : null}
          <input
            ref={ref}
            type={type}
            className={cn(
              CONTROL_CLASSES,
              'h-12',
              icon && 'ps-11',
              error && CONTROL_INVALID_CLASSES,
            )}
            {...field}
            {...props}
          />
        </div>
      )}
    </Field>
  );
});
